/**
 * Module-singleton diagnostic state for client-error reporting.
 *
 * Two stores live here:
 *
 *   1. `walletSnapshot` — a flat object describing the *current*
 *      Privy + wagmi state (last successful render of
 *      `<WalletDiagnosticSnapshot />`). Reset to defaults on module
 *      load so a fresh tab starts clean.
 *
 *   2. `eventRing` — a fixed-size circular buffer of the last
 *      `EVENT_RING_CAPACITY` state-transition events
 *      (`wallet.connected`, `wallet.address-changed`,
 *      `wallet.chain-changed`, `route.changed`, etc.). The point is
 *      to give the post-mortem a *sequence* — "what happened in the
 *      last 200ms before the loop?" — not just a final-state
 *      snapshot. The ring is small (50 entries by default) so the
 *      overhead stays trivial even on a hot loop.
 *
 * Both stores are read by `report-client-error.ts` at the moment a
 * client error is captured, *not* at the moment of the state change
 * — so even if the providers tree has fallen over by the time the
 * error boundary fires, we still have the last known good values
 * from before the throw. The observer that writes to the snapshot
 * (`<WalletDiagnosticSnapshot />`) lives inside the providers tree,
 * so once the tree is dead it stops updating, but the *previous*
 * write survives in module memory until page reload.
 *
 * No React, no providers, no side effects. Safe to import from any
 * module (including the boundary code that runs after the tree has
 * thrown).
 */

import { resetBootMilestonesForTests } from './boot-milestones';

export interface WalletSnapshot {
  /** Privy `usePrivy().ready` — false until the SDK has hydrated. */
  privyReady: boolean;
  /** Privy `usePrivy().authenticated`. */
  privyAuthenticated: boolean;
  /** First 8 chars of `user.id` so the same session can be cross-referenced across reports without storing the whole UUID. */
  privyUserIdShort: string | null;
  /** Number of items in `user.linkedAccounts` (wallet, twitter_oauth, farcaster, …). */
  linkedAccountCount: number;
  /** Distinct types in `user.linkedAccounts` — useful when the bug is shape-driven (e.g. only triggers when `twitter_oauth` is linked). */
  linkedAccountTypes: string[];
  /** True when at least one linked account is a Privy embedded wallet. */
  hasEmbeddedWallet: boolean;
  /** Lower-cased wagmi address truncated to `0xabcd…1234` form. */
  wagmiAddressShort: string | null;
  /** wagmi `useAccount().chainId`. */
  wagmiChainId: number | null;
  /** wagmi `useAccount().isConnected`. */
  wagmiConnected: boolean;
  /** wagmi connector id (`metamask`, `walletConnect`, `coinbaseWallet`, …). */
  wagmiConnectorId: string | null;
  /** wagmi connection status (`connected` | `connecting` | `reconnecting` | `disconnected`). */
  wagmiStatus: string | null;
  /** Total wallets Privy `useWallets()` returns. */
  walletCount: number;
  /** Distinct `walletClientType` values from `useWallets()` (e.g. `metamask`, `privy`, `walletconnect`). */
  walletClientTypes: string[];
  /** When `<WalletDiagnosticSnapshot />` last wrote to this object. `null` when never written. */
  lastUpdatedAt: number | null;
  /** Monotonic counter so the consumer can tell "still updating" from "stuck". */
  updateCount: number;
}

export interface DiagnosticEvent {
  ts: number;
  category: 'wallet' | 'router' | 'app' | 'render';
  msg: string;
  data?: Record<string, unknown>;
}

const EVENT_RING_CAPACITY = 50;

const defaultSnapshot: WalletSnapshot = {
  privyReady: false,
  privyAuthenticated: false,
  privyUserIdShort: null,
  linkedAccountCount: 0,
  linkedAccountTypes: [],
  hasEmbeddedWallet: false,
  wagmiAddressShort: null,
  wagmiChainId: null,
  wagmiConnected: false,
  wagmiConnectorId: null,
  wagmiStatus: null,
  walletCount: 0,
  walletClientTypes: [],
  lastUpdatedAt: null,
  updateCount: 0,
};

const walletSnapshot: WalletSnapshot = { ...defaultSnapshot };

const eventRing: DiagnosticEvent[] = [];
let eventRingHead = 0;

/**
 * Truncate a 0x-prefixed address to `0xabcd…1234` (10 chars total).
 * `null` when input is null/undefined/empty so consumers can pass
 * raw values without pre-checking.
 */
export function shortAddress(addr: string | null | undefined): string | null {
  if (!addr) return null;
  if (addr.length < 10) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/**
 * Update the wallet snapshot. Shallow-merges the partial into the
 * current state, bumps `updateCount`, stamps `lastUpdatedAt`.
 *
 * Called from `<WalletDiagnosticSnapshot />` on every meaningful
 * state change; safe to call from any client-side context. No-op
 * when called before any partial would change a field (the observer
 * already gates its own effect dependencies, so this is a soft
 * second line of defence — useful when called manually from a
 * tutorial / debug surface).
 */
export function setWalletSnapshot(partial: Partial<WalletSnapshot>): void {
  // Loose-typed view of the singleton so we can assign by string
  // key without TS complaining about union narrowing across the
  // mixed-typed snapshot fields. Safe because the only writers in
  // the codebase are this function and `resetDiagnosticState`.
  const target = walletSnapshot as unknown as Record<string, unknown>;
  let changed = false;
  for (const key of Object.keys(partial) as Array<keyof WalletSnapshot>) {
    const next = partial[key];
    const prev = walletSnapshot[key];
    if (Array.isArray(next) && Array.isArray(prev)) {
      if (
        next.length !== prev.length ||
        next.some((v, i) => v !== prev[i])
      ) {
        target[key] = [...next];
        changed = true;
      }
    } else if (next !== prev) {
      target[key] = next;
      changed = true;
    }
  }
  if (changed) {
    walletSnapshot.lastUpdatedAt = Date.now();
    walletSnapshot.updateCount += 1;
  }
}

export function getWalletSnapshot(): WalletSnapshot {
  // Return a shallow clone — callers should not mutate the singleton.
  return {
    ...walletSnapshot,
    linkedAccountTypes: [...walletSnapshot.linkedAccountTypes],
    walletClientTypes: [...walletSnapshot.walletClientTypes],
  };
}

/**
 * Append an event to the ring buffer. Old entries roll off once
 * the ring is full. `data` is JSON-serialised at log time, so keep
 * it small (no full Privy user objects please).
 */
export function pushDiagnosticEvent(
  category: DiagnosticEvent['category'],
  msg: string,
  data?: Record<string, unknown>,
): void {
  const entry: DiagnosticEvent = {
    ts: Date.now(),
    category,
    msg,
    data,
  };
  if (eventRing.length < EVENT_RING_CAPACITY) {
    eventRing.push(entry);
  } else {
    eventRing[eventRingHead] = entry;
    eventRingHead = (eventRingHead + 1) % EVENT_RING_CAPACITY;
  }
}

/**
 * Read the ring buffer in chronological order (oldest first). Safe
 * to call repeatedly; cheap, no allocation churn beyond the
 * returned array itself.
 */
export function getDiagnosticEvents(): DiagnosticEvent[] {
  if (eventRing.length < EVENT_RING_CAPACITY) {
    return [...eventRing];
  }
  // Ring is full — start from `head` and wrap.
  return [
    ...eventRing.slice(eventRingHead),
    ...eventRing.slice(0, eventRingHead),
  ];
}

/**
 * Reset everything to defaults. Test-only / debug-toggle hook; not
 * called from production code. Exported so a debug surface or a
 * unit test can wipe state between scenarios.
 */
export function resetDiagnosticState(): void {
  Object.assign(walletSnapshot, defaultSnapshot);
  eventRing.length = 0;
  eventRingHead = 0;
  resetBootMilestonesForTests();
}
